import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { formatPrice } from '@components/ProductOffers/modals/ShoppingCartDrawer/utils/cartItemCard.utils';
import type { CartHardwareContentProps } from '@components/ProductOffers/modals/ShoppingCartDrawer/CartItemCard/CartItemCard.types';
import { ExpandableSectionContainer } from '@components/ProductOffers/modals/ShoppingCartDrawer/ShoppingCartDrawer.styles';
import { ChannelLogo } from '@shared/components/display/ChannelLogo/ChannelLogo';
import { QuantityCounter } from '@shared/components/inputs/QuantityCounter/QuantityCounter';
import { DiscountInfo } from '@shared/components/display/DiscountInfo/DiscountInfo';
import { Markdown } from '@shared/components/typography/Markdown/Markdown';
import { PromoPriceDisplay } from '@shared/components/display/PromoPriceDisplay/PromoPriceDisplay';

const CartHardwareContent = ({ items, onUpdateChildQuantity }: CartHardwareContentProps) => {
  const theme = useTheme();

  return (
    <ExpandableSectionContainer>
      {items.length === 0 ? (
        <Typography variant="body.sm" color={theme.novus.mutedText}>
          No equipment
        </Typography>
      ) : (
        items.map((hw, i) => {
          return (
            <Stack
              key={hw.key}
              component={Paper}
              variant="cartItemSurface"
              justifyContent="center"
              alignItems="flex-start"
              spacing={1.875}
              useFlexGap
              sx={{ p: '15px', mb: i < items.length - 1 ? '12px' : 0 }}
            >
              {/* Image | details | price on tablet+, stacked into a column on mobile so the
                  equipment description gets full width instead of wrapping one character per line. */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'flex-start' }}
                spacing={1.875}
                useFlexGap
                sx={{ alignSelf: 'stretch' }}
              >
                {/* Image */}
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 103,
                    height: 81,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ChannelLogo src={hw.imageUrl} name={hw.name} />
                </Box>

                {/* Name + Description */}
                <Stack justifyContent="center" spacing={1.25} useFlexGap sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="label.button" color={theme.colors.black}>
                    {hw.name}
                  </Typography>
                  {hw.description && (
                    <Typography variant="body.sm" sx={{ color: theme.colors.black, lineHeight: 1.3 }}>
                      <Markdown text={hw.description} />
                    </Typography>
                  )}
                </Stack>

                {/* Price + Discount + Quantity: right-aligned beside the details on tablet+, left-aligned beneath them when stacked */}
                <Stack justifyContent="center" alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={0.5} useFlexGap sx={{ flexShrink: 0 }}>
                  <PromoPriceDisplay
                    regularPrice={hw.price}
                    freeLabel={hw.isFree ? 'Free' : undefined}
                    color={theme.colors.black}
                  />
                  <DiscountInfo promo={hw.promo} discountTag={hw.discountTag} discountMessage={hw.discountMessage} />

                  {hw.supportsQuantity && !hw.isFree && (
                    <Stack
                      justifyContent="center"
                      alignItems={{ xs: 'flex-start', md: 'flex-end' }}
                      useFlexGap
                      sx={{ gap: '5px', mt: '6px' }}
                    >
                      <Stack direction="row" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} alignItems="center" spacing={1.25} useFlexGap>
                        <Typography variant="label.fieldSmall" color={theme.colors.primary}>
                          Quantity:
                        </Typography>
                        <QuantityCounter
                          value={hw.quantity}
                          min={1}
                          max={10}
                          onChange={(newQty) => onUpdateChildQuantity?.(hw.childItemId, hw.offeringId, hw.name, newQty)}
                        />
                      </Stack>
                      <Typography variant="body.sm" sx={{ color: theme.colors.black, lineHeight: 1.3 }}>
                        {`Each quantity adds ${formatPrice(hw.unitPrice)} / month`}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Stack>
          );
        })
      )}
    </ExpandableSectionContainer>
  );
};

export default CartHardwareContent;
